import type { ReactNode } from "react";
import { createContext, useContext, useState } from "react";

// Note: circular dependency with shared/ui might occur if we import from @/shared/ui here.
// But NotificationProvider needs ToastContainer and Modal.
// Actually, ToastContainer is what uses useNotification.
// So NotificationProvider should NOT import ToastContainer if ToastContainer is in shared/ui.
// Wait, NotificationProvider IS the one that renders them.
// This is a common FSD dilemma.
// If NotificationProvider is in shared/lib, and it renders shared/ui components, it's fine.
// But if shared/ui components use useNotification (which is in shared/lib), it's ALSO fine.

import { Button, Modal } from "@/shared/ui";
import { ToastContainer } from "@/shared/ui/Toast/ToastContainer";

interface ToastNotification {
  id: string;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message?: string;
  duration?: number;
}

interface ConfirmModal {
  id: string;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  variant?: "danger" | "primary";
}

interface NotificationContextType {
  toasts: ToastNotification[];
  showToast: (toast: Omit<ToastNotification, "id">) => void;
  removeToast: (id: string) => void;
  confirmModal: ConfirmModal | null;
  showConfirm: (modal: Omit<ConfirmModal, "id">) => void;
  hideConfirm: () => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
  addNotification?: (notification: any) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  const [confirmModal, setConfirmModal] = useState<ConfirmModal | null>(null);

  const showToast = (toast: Omit<ToastNotification, "id">): void => {
    const id = Date.now().toString();
    const newToast = { ...toast, id };
    setToasts((prev) => [...prev, newToast]);
    const duration = toast.duration || 5000;
    setTimeout(() => {
      removeToast(id);
    }, duration);
  };

  const removeToast = (id: string): void => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const showConfirm = (modal: Omit<ConfirmModal, "id">): void => {
    const id = Date.now().toString();
    setConfirmModal({ ...modal, id });
  };

  const hideConfirm = (): void => {
    setConfirmModal(null);
  };

  const showSuccess = (title: string, message?: string): void => {
    showToast({ type: "success", title, message });
  };

  const showError = (title: string, message?: string): void => {
    showToast({ type: "error", title, message });
  };

  const showWarning = (title: string, message?: string): void => {
    showToast({ type: "warning", title, message });
  };

  const showInfo = (title: string, message?: string): void => {
    showToast({ type: "info", title, message });
  };

  const addNotification = (notification: any): void => {
    if (notification.type && notification.message) {
      showToast({ type: notification.type, title: notification.message });
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        toasts,
        showToast,
        removeToast,
        confirmModal,
        showConfirm,
        hideConfirm,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        addNotification,
      }}
    >
      {children}
      <ToastContainer />
      {confirmModal && (
        <Modal
          open={!!confirmModal}
          onClose={() => {
            confirmModal.onCancel?.();
            hideConfirm();
          }}
          title={confirmModal.title}
          widthClassName="max-w-md"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {confirmModal.message}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  confirmModal.onCancel?.();
                  hideConfirm();
                }}
              >
                {confirmModal.cancelText || "취소"}
              </Button>
              <Button
                variant={
                  confirmModal.variant === "danger" ? "destructive" : "default"
                }
                onClick={() => {
                  confirmModal.onConfirm();
                  hideConfirm();
                }}
              >
                {confirmModal.confirmText || "확인"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error("NotificationProvider 안에 정의되어야 합니다");
  }
  return context;
}
